import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import classNames from "classnames";
import {
  FocusEvent,
  KeyboardEvent,
  memo,
  MouseEvent,
  ReactNode,
  RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
} from "react";
import { RedoButton } from "../buttons/redo-button";
import { Dropdown } from "../dropdown";
import { Flex } from "../flex";
import { RedoIcon } from "../icon/redo-icon";
import { RedoSpinner } from "../loading/redo-spinner";
import { Text } from "../text";
import { Tooltip } from "../tooltip/tooltip";
import {
  RedoTextareaValidationIssue,
  RedoTextareaValidationStatus,
} from "./redo-textarea-validation";
import * as css from "./redo-textarea-validation-overlay.module.css";

/** Mirrors the textarea's text behind it so flagged ranges can be styled. */
export const TextareaHighlightLayer = memo(function TextareaHighlightLayer({
  textareaRef,
  textClassName,
  value,
  issues,
  activeIssue,
  setActiveMark,
  onHoverIssue,
}: {
  textareaRef: RefObject<HTMLTextAreaElement>;
  textClassName: string;
  value: string;
  issues: readonly RedoTextareaValidationIssue[];
  activeIssue: RedoTextareaValidationIssue | undefined;
  setActiveMark(mark: HTMLElement | null): void;
  onHoverIssue(issue: RedoTextareaValidationIssue | undefined): void;
}) {
  const layerRef = useRef<HTMLDivElement>(null);
  const hoveredIndex = useRef(-1);

  const sync = useHandler(() => {
    const layer = layerRef.current;
    const textarea = textareaRef.current;
    if (!layer || !textarea) {
      return;
    }
    layer.style.width = `${textarea.clientWidth}px`;
    layer.style.height = `${textarea.clientHeight}px`;
    layer.scrollTop = textarea.scrollTop;
  });

  // The textarea sits on top and takes the pointer, so hit-test the marks beneath it.
  const reportHover = useHandler((event: globalThis.MouseEvent | null) => {
    const marks = Array.from(layerRef.current?.querySelectorAll("mark") ?? []);
    const index = event
      ? marks.findIndex((mark) =>
          Array.from(mark.getClientRects()).some(
            (rect) =>
              rect.left <= event.clientX &&
              event.clientX <= rect.right &&
              rect.top <= event.clientY &&
              event.clientY <= rect.bottom,
          ),
        )
      : -1;
    if (index !== hoveredIndex.current) {
      hoveredIndex.current = index;
      onHoverIssue(issues[index]);
    }
  });
  const clearHover = useHandler(() => reportHover(null));

  useEffect(() => {
    hoveredIndex.current = -1;
  }, [issues]);

  useLayoutEffect(() => {
    sync();
  });

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }
    sync();
    const resizeObserver = new ResizeObserver(sync);
    resizeObserver.observe(textarea);
    textarea.addEventListener("scroll", sync);
    textarea.addEventListener("mousemove", reportHover);
    textarea.addEventListener("mouseleave", clearHover);
    return () => {
      resizeObserver.disconnect();
      textarea.removeEventListener("scroll", sync);
      textarea.removeEventListener("mousemove", reportHover);
      textarea.removeEventListener("mouseleave", clearHover);
    };
  }, [textareaRef, sync, reportHover, clearHover]);

  const segments: ReactNode[] = [];
  let cursor = 0;
  for (const [index, issue] of issues.entries()) {
    segments.push(value.slice(cursor, issue.start));
    segments.push(
      <mark
        className={classNames(
          css.highlight,
          issue === activeIssue && css.active,
        )}
        key={index}
        ref={issue === activeIssue ? setActiveMark : undefined}
      >
        {value.slice(issue.start, issue.end)}
      </mark>,
    );
    cursor = issue.end;
  }
  // The trailing space keeps a final empty line, as the textarea renders one.
  segments.push(value.slice(cursor), " ");

  return (
    <div
      aria-hidden
      className={classNames(textClassName, css.highlightLayer)}
      ref={layerRef}
    >
      {segments}
    </div>
  );
});

function keepTextareaFocus(event: MouseEvent) {
  event.preventDefault();
}

export const TextareaIssuePopover = memo(function TextareaIssuePopover({
  anchor,
  issue,
  popoverRef,
  canResolveFix,
  fixing,
  fixFailed,
  handlers,
}: {
  anchor: HTMLElement | null;
  issue: RedoTextareaValidationIssue | undefined;
  popoverRef: RefObject<HTMLDivElement>;
  /** Fix is offered for issues without a replacement, written when clicked. */
  canResolveFix: boolean;
  fixing: boolean;
  fixFailed: boolean;
  handlers: {
    onFix(issue: RedoTextareaValidationIssue): Promise<void>;
    onIgnore(issue: RedoTextareaValidationIssue): void;
    onKeyDown(event: KeyboardEvent<HTMLDivElement>): void;
    onBlur(event: FocusEvent<HTMLDivElement>): void;
    onMouseEnter(): void;
    onMouseLeave(): void;
  };
}) {
  const { t } = useTranslation();
  const messageId = useId();

  if (!anchor || !issue) {
    return null;
  }

  // Unmounted rather than closed: a Fix removes the anchor mark mid close-animation.
  return (
    <Dropdown
      anchor={anchor}
      className={css.issuePopover}
      fitToAnchor={false}
      flexProps={{
        role: "dialog",
        "aria-labelledby": messageId,
        onMouseDown: keepTextareaFocus,
        onMouseEnter: handlers.onMouseEnter,
        onMouseLeave: handlers.onMouseLeave,
        onKeyDown: handlers.onKeyDown,
        onBlur: handlers.onBlur,
      }}
      offsetY={8}
      open
      placement="bottom-start"
      ref={popoverRef}
    >
      <Flex dir="column" gap="lg" p="xs">
        <div id={messageId}>
          <Text fontSize="sm" textColor="primary">
            {issue.message}
          </Text>
        </div>
        {fixFailed && (
          <Text fontSize="sm" textColor="secondary">
            {t("Couldn't write a fix. You can edit it yourself.", {
              context: "text validation",
            })}
          </Text>
        )}
        <Flex align="center" dir="row" gap="sm">
          {(issue.replacement !== undefined || canResolveFix) && (
            <RedoButton
              hierarchy="primary"
              onClick={() => void handlers.onFix(issue)}
              pending={fixing}
              size="sm"
              text={t("Fix", { context: "text validation" })}
            />
          )}
          <RedoButton
            hierarchy="tertiary"
            onClick={() => handlers.onIgnore(issue)}
            size="sm"
            text={t("Ignore", { context: "text validation" })}
          />
        </Flex>
      </Flex>
    </Dropdown>
  );
});

export const TextareaValidationStatusIcon = memo(
  function TextareaValidationStatusIcon({
    status,
  }: {
    status: RedoTextareaValidationStatus;
  }) {
    const { t } = useTranslation();
    const label = {
      idle: "",
      loading: t("Checking…", { context: "text validation" }),
      valid: t("No issues found", { context: "text validation" }),
      invalid: t("Review the highlighted text", { context: "text validation" }),
    }[status];

    return (
      <Tooltip title={label}>
        <Flex
          align="center"
          aria-hidden
          className={css.statusIcon}
          data-status={status}
          justify="center"
        >
          {status === "loading" && <RedoSpinner height={16} width={16} />}
          {status === "valid" && (
            <RedoIcon color="success" Icon={CheckCircleIcon} size={16} />
          )}
          {status === "invalid" && (
            <RedoIcon color="error" Icon={AlertTriangleIcon} size={16} />
          )}
        </Flex>
      </Tooltip>
    );
  },
);
